import {
  entitlementsFor,
  hasModule,
  moduleAccess,
  type Entitlements,
  type PlanKey,
  type PlanModule,
  type SubscriptionSnapshot,
  type SubscriptionStatus,
} from '@tailoring-crm/shared';
import { useAuth } from '../auth/auth-context.js';

/**
 * What this shop may do, as the BROWSER sees it — read from the same service the API refuses
 * with.
 *
 * That is the whole point of this file. It computes nothing of its own: it takes the plan and
 * subscription status the session already carries, hands them to `@tailoring-crm/shared`, and
 * returns what comes back. If the UI reimplemented the tiers, a hidden button would eventually
 * appear in front of an open endpoint — a security bug — or a visible one in front of a closed
 * endpoint, which is a support call. Reading the same functions makes both impossible.
 *
 * It needs no network call. `/auth/me` already carries the shop's plan, status and trial end,
 * so every screen can ask "may I show this?" without waiting on a request — which matters,
 * because a nav item that appears half a second late reads as a glitch.
 */

/** The snapshot, rebuilt from the session. `null` when nobody is signed into a shop. */
export function useSubscriptionSnapshot(): SubscriptionSnapshot | null {
  const { shop } = useAuth();
  if (!shop) return null;
  return {
    // The session's `plan` and `subscriptionStatus` are strings on the wire; the values come
    // from the same enums the server validates against, so this is a narrowing, not a guess.
    planKey: shop.plan as PlanKey,
    status: shop.subscriptionStatus as SubscriptionStatus,
    trialEndsAt: shop.trialEndsAt,
    currentPeriodEndsAt: shop.currentPeriodEndsAt ?? null,
  };
}

/**
 * The whole entitlement picture for the current shop, or `null` before there is one.
 *
 * `now` is read once per render rather than kept on a timer: a trial does not expire under
 * somebody's fingers in a way a re-render would need to catch, and a component that re-renders
 * every second to count a fortnight down is a battery bug on a shop counter's tablet.
 */
export function useEntitlements(): Entitlements | null {
  const snapshot = useSubscriptionSnapshot();
  return snapshot ? entitlementsFor(snapshot) : null;
}

/** True when the module is in the plan — the question navigation and links ask. */
export function useHasModule(module: PlanModule): boolean {
  const snapshot = useSubscriptionSnapshot();
  // Nothing is hidden before the session has loaded; the API is still the one refusing.
  return snapshot ? hasModule(snapshot, module) : true;
}

/**
 * True when the shop may CHANGE data in this module — what a save button asks.
 *
 * Deliberately different from {@link useHasModule}: a shop whose card has failed still SEES
 * its expense book, because the data is theirs and hiding it would look like loss. What it
 * cannot do is add to it.
 */
export function useCanWrite(module: PlanModule): boolean {
  const snapshot = useSubscriptionSnapshot();
  return snapshot ? moduleAccess(snapshot, module).mode === 'full' : true;
}

/** Why writing is refused, for a screen that wants to explain rather than just disable. */
export function useModuleAccess(module: PlanModule): ReturnType<typeof moduleAccess> | null {
  const snapshot = useSubscriptionSnapshot();
  return snapshot ? moduleAccess(snapshot, module) : null;
}
